import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Plus, Calendar, MapPin, Users, Clock, Rows, Calendar as CalendarIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NuevaMovilizacionDialog } from '@/components/NuevaMovilizacionDialog';
import { MovilizacionCard } from '@/components/MovilizacionCard';
import { CalendarView } from '@/components/CalendarView';
import { LoadingOrEmpty } from '@/components/LoadingOrEmpty';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardPage = () => {
  const { user } = useAuth();
  const [movilizaciones, setMovilizaciones] = useState([]);
  const [calendario, setCalendario] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('todas');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [editingMov, setEditingMov] = useState(null);

  const fetchMovilizaciones = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/movilizaciones`, {
        withCredentials: true
      });
      setMovilizaciones(data);
    } catch (error) {
      toast.error('Error al cargar movilizaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalendario = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/calendario`, {
        withCredentials: true
      });
      setCalendario(data);
    } catch (error) {
      // silent — calendar is a secondary view
    }
  }, []);

  useEffect(() => {
    fetchMovilizaciones();
    fetchCalendario();
  }, [fetchMovilizaciones, fetchCalendario]);

  useEffect(() => {
    if (viewMode !== 'calendar') return undefined;
    const id = setInterval(fetchCalendario, 30000);
    return () => clearInterval(id);
  }, [viewMode, fetchCalendario]);

  const openCreateDialog = () => {
    setEditingMov(null);
    setShowDialog(true);
  };

  const openEditDialog = (mov) => {
    setEditingMov(mov);
    setShowDialog(true);
  };

  const handleDialogSuccess = () => {
    fetchMovilizaciones();
    fetchCalendario();
    setEditingMov(null);
  };

  const handleDeleteMovilizacion = async (mov) => {
    try {
      await axios.delete(`${API_URL}/api/movilizaciones/${mov.id}`, {
        withCredentials: true,
      });
      toast.success('Solicitud eliminada');
      fetchMovilizaciones();
      fetchCalendario();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo eliminar la solicitud');
      throw error;
    }
  };

  const filterByEstado = (estado) => {
    if (estado === 'todas') return movilizaciones;
    return movilizaciones.filter((m) => m.estado === estado);
  };

  const getStats = () => {
    return {
      total: movilizaciones.length,
      agendadas: movilizaciones.filter((m) => m.estado === 'agendado').length,
      en_espera: movilizaciones.filter((m) => m.estado === 'en_espera_asignacion').length,
      asignadas: movilizaciones.filter((m) => m.estado === 'asignado').length,
      realizadas: movilizaciones.filter((m) => m.estado === 'realizada').length,
      suspendidas: movilizaciones.filter((m) => m.estado === 'suspendida').length,
      rechazadas: movilizaciones.filter((m) => m.estado === 'rechazado').length
    };
  };

  const stats = getStats();

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A] mb-2">
                Mis Movilizaciones
              </h1>
              <p className="text-[#495057] font-['IBM_Plex_Sans']">
                Gestiona tus solicitudes de transporte
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex bg-white border border-[#E9ECEF] p-1">
                <Button
                  data-testid="view-list-button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`gap-2 font-['IBM_Plex_Sans'] transition-colors ${
                    viewMode === 'list'
                      ? 'bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] hover:text-white'
                      : 'hover:bg-[#F8F9FA]'
                  }`}
                >
                  <Rows size={16} weight="bold" /> Lista
                </Button>
                <Button
                  data-testid="view-calendar-button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className={`gap-2 font-['IBM_Plex_Sans'] transition-colors ${
                    viewMode === 'calendar'
                      ? 'bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] hover:text-white'
                      : 'hover:bg-[#F8F9FA]'
                  }`}
                >
                  <CalendarIcon size={16} weight="bold" /> Calendario
                </Button>
              </div>
              <Button
                data-testid="nueva-movilizacion-button"
                onClick={openCreateDialog}
                className="gap-2 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] h-12 px-6 transition-all hover:-translate-y-0.5"
              >
                <Plus size={20} weight="bold" />
                Nueva Movilización
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-6 bg-white border border-[#E9ECEF]">
              <p className="text-[#495057] text-sm font-['IBM_Plex_Sans'] mb-2">Total</p>
              <p className="text-3xl font-black font-['Chivo'] text-[#0A0A0A]">{stats.total}</p>
            </div>
            <div className="p-6 bg-white border border-[#E9ECEF]">
              <p className="text-[#6C757D] text-sm font-['IBM_Plex_Sans'] mb-2">Agendadas</p>
              <p className="text-3xl font-black font-['Chivo'] text-[#6C757D]">{stats.agendadas}</p>
            </div>
            <div className="p-6 bg-white border border-[#E9ECEF]">
              <p className="text-[#0891B2] text-sm font-['IBM_Plex_Sans'] mb-2">En Espera</p>
              <p className="text-3xl font-black font-['Chivo'] text-[#0891B2]">{stats.en_espera}</p>
            </div>
            <div className="p-6 bg-white border border-[#E9ECEF]">
              <p className="text-[#008630] text-sm font-['IBM_Plex_Sans'] mb-2">Asignadas</p>
              <p className="text-3xl font-black font-['Chivo'] text-[#008630]">{stats.asignadas}</p>
            </div>
            <div className="p-6 bg-white border border-[#E9ECEF]">
              <p className="text-[#FF3B30] text-sm font-['IBM_Plex_Sans'] mb-2">Rechazadas</p>
              <p className="text-3xl font-black font-['Chivo'] text-[#FF3B30]">{stats.rechazadas}</p>
            </div>
          </div>
        </div>

        {/* Content: List or Calendar */}
        {viewMode === 'calendar' ? (
          <CalendarView movilizaciones={calendario} />
        ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-[#E9ECEF] p-1 h-auto inline-flex flex-wrap">
            <TabsTrigger
              value="todas"
              data-testid="tab-todas"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Todas ({stats.total})
            </TabsTrigger>
            <TabsTrigger
              value="agendado"
              data-testid="tab-agendado"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#6C757D] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Agendadas ({stats.agendadas})
            </TabsTrigger>
            <TabsTrigger
              value="en_espera_asignacion"
              data-testid="tab-en-espera"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#0891B2] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              En Espera ({stats.en_espera})
            </TabsTrigger>
            <TabsTrigger
              value="asignado"
              data-testid="tab-asignado"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#008630] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Asignadas ({stats.asignadas})
            </TabsTrigger>
            <TabsTrigger
              value="realizada"
              data-testid="tab-realizada"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#065F46] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Realizadas ({stats.realizadas})
            </TabsTrigger>
            <TabsTrigger
              value="suspendida"
              data-testid="tab-suspendida"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#78716C] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Suspendidas ({stats.suspendidas})
            </TabsTrigger>
            <TabsTrigger
              value="rechazado"
              data-testid="tab-rechazado"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Rechazadas ({stats.rechazadas})
            </TabsTrigger>
          </TabsList>

          {['todas', 'agendado', 'en_espera_asignacion', 'asignado', 'realizada', 'suspendida', 'rechazado'].map((estado) => {
            const filtered = filterByEstado(estado);
            const emptyMsg = estado === 'todas'
              ? 'No hay movilizaciones'
              : `No hay movilizaciones en estado "${estado}"`;
            return (
              <TabsContent key={estado} value={estado} className="mt-6">
                <LoadingOrEmpty
                  loading={loading}
                  isEmpty={filtered.length === 0}
                  emptyIcon={<Calendar size={48} className="text-[#E9ECEF] mx-auto mb-4" weight="bold" />}
                  emptyMessage={emptyMsg}
                  testId="dashboard"
                >
                  <div className="grid grid-cols-1 gap-4">
                    {filtered.map((mov) => (
                      <MovilizacionCard
                        key={mov.id}
                        movilizacion={mov}
                        onUpdate={fetchMovilizaciones}
                        onEdit={openEditDialog}
                        onDelete={handleDeleteMovilizacion}
                      />
                    ))}
                  </div>
                </LoadingOrEmpty>
              </TabsContent>
            );
          })}
        </Tabs>
        )}
      </div>

      <NuevaMovilizacionDialog
        open={showDialog}
        onOpenChange={(v) => { setShowDialog(v); if (!v) setEditingMov(null); }}
        onSuccess={handleDialogSuccess}
        existingMov={editingMov}
      />
    </div>
  );
};